import { useState, useCallback } from "react";
import axiosInstance from "../../../axios/axiosInstance";
import {
  normalizeCertifications,
  sanitizeCareerDetailsPayload,
  sanitizeChooseRolesPayload,
} from "../utils/profileFieldNormalization";

/**
 * Custom hook to load and save a candidate's full profile data.
 * Uses NEW dedicated admin endpoints:
 *   GET  /api/admin/candidates/:userId/profile
 *   PUT  /api/admin/candidates/:userId/profile
 *
 * The save is a placeholder until the backend implements the PUT endpoint.
 */
const useCandidateProfileData = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");
      // NEW endpoint — dedicated admin profile fetch
      const response = await axiosInstance.get(
        `/api/admin/candidates/${userId}/profile`
      );
      const profile = response.data;
      if (profile?.careerDetails?.certifications) {
        profile.careerDetails.certifications = normalizeCertifications(
          profile.careerDetails.certifications,
        );
      }
      setProfileData(profile);
    } catch (err) {
      console.error("Error fetching candidate profile:", err);
      setError(
        err?.response?.data?.error || "Failed to load candidate profile."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save candidate profile data.
   * @param {string|number} userId
   * @param {string} section  - "completeProfile" | "careerDetails" | "chooseRoles"
   * @param {object} data     - the updated section data
   *
   * NOTE: The PUT endpoint is not yet implemented on the backend.
   *       The function logs the payload and resolves immediately (placeholder).
   *       Replace the placeholder block with the real axiosInstance.put() call
   *       once the backend endpoint is ready.
   */
  const saveProfile = useCallback(async (userId, section, data) => {
    setSaving(true);
    setError("");
    try {
      const sanitizedData =
        section === "chooseRoles"
          ? sanitizeChooseRolesPayload(data)
          : section === "careerDetails"
            ? sanitizeCareerDetailsPayload(data)
            : data;

      await axiosInstance.put(`/api/admin/candidates/${userId}/profile`, {
        section,
        data: sanitizedData,
      });

      const targetKey =
        section === "completeProfile"
          ? "personalInfo"
          : section === "chooseRoles"
            ? "preferredRoles"
            : section;

      // Merge saved data into local state so UI stays in sync
      setProfileData((prev) => ({
        ...prev,
        [targetKey]: { ...(prev?.[targetKey] || {}), ...sanitizedData },
      }));

      return { success: true };
    } catch (err) {
      console.error("Error saving candidate profile:", err);
      const msg = err?.response?.data?.error || "Failed to save profile.";
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, []);

  return { profileData, loading, saving, error, fetchProfile, saveProfile };
};

export default useCandidateProfileData;
